import { useEffect, useRef, type KeyboardEvent, type ReactElement } from 'react';
import { ActionIcon, Badge, Box, Group, Switch, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from 'lucide-react';
import type { TaxonomyRowView } from './UsageCategoryTaxonomyTypes';
import { canAddBeneathDepth } from './UsageCategoryTaxonomyTypes';

interface TaxonomyTreeTableProps {
    rows: TaxonomyRowView[];
    focusedDraftId: string | null;
    editingDraftId: string | null;
    onFocusRow: (draftId: string) => void;
    onStartEdit: (draftId: string) => void;
    onStopEdit: () => void;
    onSetExpanded: (draftId: string, value: boolean) => void;
    onRename: (draftId: string, label: string) => void;
    onToggleEnabled: (draftId: string) => void;
    onRemove: (draftId: string) => void;
    onAddChild: (parentDraftId: string) => void;
    onAssist: (draftId: string) => void;
}

const INDENT_PX = 20;

/**
 * Single hierarchical tree-table for the usage-category taxonomy. Rows are
 * keyboard-navigable via a roving tabindex (Up/Down move, Right expand / first
 * child, Left collapse / parent, Enter or F2 edit, Space toggle work-related,
 * Delete remove). Per-row controls stay hidden until the row is hovered or
 * focused so the resting view reads as a clean taxonomy.
 */
export default function TaxonomyTreeTable(props: TaxonomyTreeTableProps): ReactElement {
    const { rows, focusedDraftId, editingDraftId, onFocusRow, onSetExpanded, onStartEdit } = props;
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const isWide = useBreakpointUp('sm');
    const indent = isWide ? INDENT_PX : 12;

    useEffect(() => {
        if (focusedDraftId === null || editingDraftId === focusedDraftId) return;
        if (!rows.some((r) => r.node.draftId === focusedDraftId)) return;
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
        rowRefs.current.get(focusedDraftId)?.focus();
    }, [rows, focusedDraftId, editingDraftId]);

    const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: TaxonomyRowView, index: number): void => {
        const id = row.node.draftId;
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                const next = rows[index + 1];
                if (next) onFocusRow(next.node.draftId);
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                const prev = rows[index - 1];
                if (prev) onFocusRow(prev.node.draftId);
                break;
            }
            case 'ArrowRight': {
                event.preventDefault();
                if (!row.hasChildren) break;
                if (!row.expanded) onSetExpanded(id, true);
                else {
                    const child = rows[index + 1];
                    if (child && child.parentDraftId === id) onFocusRow(child.node.draftId);
                }
                break;
            }
            case 'ArrowLeft': {
                event.preventDefault();
                if (row.hasChildren && row.expanded) onSetExpanded(id, false);
                else if (row.parentDraftId !== null) onFocusRow(row.parentDraftId);
                break;
            }
            case 'Enter':
            case 'F2': {
                event.preventDefault();
                onStartEdit(id);
                break;
            }
            case ' ': {
                event.preventDefault();
                props.onToggleEnabled(id);
                break;
            }
            case 'Delete': {
                event.preventDefault();
                props.onRemove(id);
                break;
            }
        }
    };

    return (
        <Table highlightOnHover withTableBorder verticalSpacing={8} horizontalSpacing={isWide ? 'md' : 'xs'} layout="fixed">
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Category</Table.Th>
                    <Table.Th w={isWide ? 96 : 52}>Enabled</Table.Th>
                    <Table.Th w={isWide ? 68 : 60} />
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.map((row, index) => {
                    const id = row.node.draftId;
                    const focused = id === focusedDraftId;
                    const editing = id === editingDraftId;
                    return (
                        <Table.Tr
                            key={id}
                            className="usage-cat-row"
                            ref={(el) => {
                                if (el) rowRefs.current.set(id, el);
                                else rowRefs.current.delete(id);
                            }}
                            tabIndex={focused ? 0 : -1}
                            aria-level={row.depth + 1}
                            aria-expanded={row.hasChildren ? row.expanded : undefined}
                            onFocus={() => onFocusRow(id)}
                            onClick={() => onFocusRow(id)}
                            onKeyDown={(e) => handleKeyDown(e, row, index)}
                        >
                            <Table.Td>
                                <Group gap={6} wrap="nowrap" style={{ paddingLeft: row.depth * indent, minWidth: 0 }}>
                                    {row.hasChildren ? (
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            tabIndex={-1}
                                            aria-label={row.expanded ? 'Collapse' : 'Expand'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSetExpanded(id, !row.expanded);
                                            }}
                                        >
                                            {row.expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                                        </ActionIcon>
                                    ) : (
                                        <Box w={20} style={{ flexShrink: 0 }} />
                                    )}

                                    {editing ? (
                                        <TextInput
                                            size="xs"
                                            autoFocus
                                            aria-label={`Rename ${row.node.label}`}
                                            value={row.node.label}
                                            onChange={(e) => props.onRename(id, e.currentTarget.value)}
                                            onBlur={props.onStopEdit}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === 'Escape') {
                                                    e.preventDefault();
                                                    props.onStopEdit();
                                                }
                                                e.stopPropagation();
                                            }}
                                            style={{ flex: 1 }}
                                        />
                                    ) : (
                                        <Group
                                            gap="xs"
                                            wrap="nowrap"
                                            style={{ flex: 1, minWidth: 0 }}
                                            onDoubleClick={() => onStartEdit(id)}
                                        >
                                            <Text
                                                size="sm"
                                                fw={row.depth === 0 ? 600 : 400}
                                                truncate
                                                title={row.node.label}
                                                c={row.node.enabled ? undefined : 'dimmed'}
                                                td={row.node.enabled ? undefined : 'line-through'}
                                            >
                                                {row.node.label}
                                            </Text>
                                            {row.node.status === 'edited' && (
                                                <Badge size="xs" variant="light" color="yellow">
                                                    edited
                                                </Badge>
                                            )}
                                        </Group>
                                    )}
                                </Group>
                            </Table.Td>

                            <Table.Td>
                                <Box style={{ position: 'relative', height: 22 }}>
                                    {isWide && !row.node.enabled && (
                                        <Text className="usage-cat-rest" size="xs" c="dimmed" style={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)' }}>
                                            Disabled
                                        </Text>
                                    )}
                                    <Switch
                                        className="usage-cat-control"
                                        size="xs"
                                        tabIndex={-1}
                                        checked={row.node.enabled}
                                        onChange={() => props.onToggleEnabled(id)}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={`Enabled: ${row.node.label}`}
                                        style={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)' }}
                                    />
                                </Box>
                            </Table.Td>

                            <Table.Td>
                                <Group className="usage-cat-control" gap={2} justify="flex-end" wrap="nowrap">
                                    {canAddBeneathDepth(row.depth) && (
                                        <Tooltip label="AI assist" openDelay={400}>
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                color="gray"
                                                tabIndex={-1}
                                                aria-label="AI assist"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    props.onAssist(id);
                                                }}
                                            >
                                                <Sparkles size={15} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                    {canAddBeneathDepth(row.depth) && (
                                        <Tooltip label="Add subcategory" openDelay={400}>
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                color="gray"
                                                tabIndex={-1}
                                                aria-label="Add subcategory"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    props.onAddChild(id);
                                                }}
                                            >
                                                <Plus size={15} />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                    <Tooltip label="Remove" openDelay={400}>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            color="gray"
                                            tabIndex={-1}
                                            aria-label="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.onRemove(id);
                                            }}
                                        >
                                            <Trash2 size={15} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Table.Td>
                        </Table.Tr>
                    );
                })}
            </Table.Tbody>
        </Table>
    );
}
